import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { Delete20Regular, Search20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideRowActions, GuideTable, GuideTh, guideCellClassName as cell } from '../../../theme/components/GuideTable';
import { GuidePagination } from '../../../theme/components/GuidePagination';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { GuideBannerAction } from '../../../theme/components/GuideButtons';
import { StatusPill } from '../../../theme/components/StatusPill';
import { MedicalServiceChoiceDialog } from '../ProviderConsumptionImport/MedicalServiceChoice.Dialog';
import { getApiErrorMessage } from '../../../functions/ProviderConsumptionImport.Functions';
import { serviceSourceKeyKindOf } from '../../../functions/ProviderConsumptionImport.Review.Functions';
import { formatDateTime } from '../../../functions/ProviderConsumptions.Functions';
import {
  ProviderMedicalServiceMapping,
  SERVICE_MAPPINGS_DEFAULT_PAGE_SIZE,
  SERVICE_MAPPINGS_PAGE_SIZES,
  ServiceMappingsPage,
  ServiceMappingsQuery,
} from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import '../ProviderConsumptionImport/ProviderConsumptionImport.Styles.css';

/** Debounce de la búsqueda (igual que la revisión de ítems). */
export const SERVICE_MAPPINGS_SEARCH_DEBOUNCE_MS = 300;

export interface ProviderServiceMappingsSectionProps {
  LoadMappings: (filter: ServiceMappingsQuery) => Promise<ServiceMappingsPage>;
  UpdateMapping: (mappingId: string, medicalServiceId: number) => Promise<ProviderMedicalServiceMapping | undefined>;
  DeleteMapping: (mappingId: string) => Promise<void>;
}

/** "Recordado / clave" del servicio: "Laboratorio central [LC01]". */
const serviceText = (mapping: ProviderMedicalServiceMapping): string => {
  const name = mapping.medicalServiceName?.trim() || `#${mapping.medicalServiceId}`;
  return mapping.medicalServiceCode?.trim() ? `${name} [${mapping.medicalServiceCode.trim()}]` : name;
};

/**
 * "Correspondencias de servicios" del proveedor (spec 009, US6, FR-019): las claves recordadas al importar con "Recordar
 * para este proveedor" → servicio médico. Tabla paginada en el servidor con búsqueda, "Cambiar servicio" con el selector
 * de servicios (PUT) y "Eliminar" con confirmación (DELETE). Cambiar o eliminar no toca consumos ya importados.
 */
export const ProviderServiceMappingsSection = ({ LoadMappings, UpdateMapping, DeleteMapping }: ProviderServiceMappingsSectionProps) => {
  const { t } = useTranslation();
  const tr = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.serviceMappings.${key}`, options) as string;

  const [query, setQuery] = useState<ServiceMappingsQuery>({ pageNumber: 1, pageSize: SERVICE_MAPPINGS_DEFAULT_PAGE_SIZE });
  const [text, setText] = useState('');
  const [items, setItems] = useState<ProviderMedicalServiceMapping[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(undefined);
  const [reloadToken, setReloadToken] = useState(0);
  const requestId = useRef(0);
  const timer = useRef<number | undefined>(undefined);

  const [editing, setEditing] = useState<ProviderMedicalServiceMapping | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(undefined);
  const [deleting, setDeleting] = useState<ProviderMedicalServiceMapping | undefined>(undefined);
  const [removing, setRemoving] = useState(false);
  const [deleteError, setDeleteError] = useState<unknown>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const callbacks = useRef(LoadMappings);
  callbacks.current = LoadMappings;

  const load = useCallback(() => {
    const id = ++requestId.current;
    setLoading(true);
    setLoadError(undefined);
    callbacks
      .current(query)
      .then((page) => {
        if (id !== requestId.current) return;
        setItems(page.items);
        setTotal(page.totalRecords);
      })
      .catch((error: unknown) => {
        if (id === requestId.current) setLoadError(error);
      })
      .finally(() => {
        if (id === requestId.current) {
          setLoading(false);
          setLoaded(true);
        }
      });
  }, [query]);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const onType = (value: string) => {
    setText(value);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(
      () => setQuery((current) => ({ ...current, search: value.trim() || undefined, pageNumber: 1 })),
      SERVICE_MAPPINGS_SEARCH_DEBOUNCE_MS
    );
  };

  const saveService = async (mapping: ProviderMedicalServiceMapping, medicalServiceId: number) => {
    setSaving(true);
    setSaveError(undefined);
    try {
      const updated = await UpdateMapping(mapping.id, medicalServiceId);
      setItems((current) => current.map((item) => (item.id === mapping.id ? { ...item, ...(updated ?? { medicalServiceId }) } : item)));
      setEditing(undefined);
      setNotice(tr('updated', { key: mapping.sourceKey }));
    } catch (error: unknown) {
      setSaveError(error);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const mapping = deleting;
    if (!mapping) return;
    setRemoving(true);
    setDeleteError(undefined);
    try {
      await DeleteMapping(mapping.id);
      setDeleting(undefined);
      setNotice(tr('deleted', { key: mapping.sourceKey }));
      // Si era el único de la última página, vuelve a la anterior.
      if (items.length === 1 && query.pageNumber > 1) setQuery((current) => ({ ...current, pageNumber: current.pageNumber - 1 }));
      else setReloadToken((value) => value + 1);
    } catch (error: unknown) {
      setDeleteError(error);
    } finally {
      setRemoving(false);
    }
  };

  const kindLabel = (mapping: ProviderMedicalServiceMapping): string => {
    const kind = serviceSourceKeyKindOf(mapping.sourceKeyKind);
    return kind ? tr(`kinds.${kind}`) : String(mapping.sourceKeyKind ?? '—');
  };

  const renderBody = () => {
    if (loadError !== undefined && items.length === 0) {
      return (
        <GuideMessageBar intent="error" layout="multiline" data-testid="psm-load-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('loadError')}</MessageBarTitle>
            {getApiErrorMessage(loadError)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => setReloadToken((value) => value + 1)}>{tr('retry')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      );
    }
    if (!loaded || (loading && items.length === 0)) return <GuideSkeleton rows={4} label={tr('loading')} data-testid="psm-loading" />;
    if (items.length === 0) {
      return query.search ? (
        <GuideEmptyState title={tr('noMatchTitle')} text={tr('noMatchText')} data-testid="psm-no-match" />
      ) : (
        <GuideEmptyState title={tr('emptyTitle')} text={tr('emptyText')} data-testid="psm-empty" />
      );
    }
    return (
      <GuideTable
        ariaLabel={tr('tableLabel')}
        data-testid="psm-table"
        footer={
          <GuidePagination
            page={query.pageNumber}
            pageSize={query.pageSize}
            total={total}
            pageSizeOptions={SERVICE_MAPPINGS_PAGE_SIZES}
            onPageChange={(pageNumber) => setQuery((current) => ({ ...current, pageNumber }))}
            onPageSizeChange={(pageSize) => setQuery((current) => ({ ...current, pageSize, pageNumber: 1 }))}
          />
        }
      >
        <thead>
          <tr>
            <GuideTh>{tr('columns.key')}</GuideTh>
            <GuideTh>{tr('columns.kind')}</GuideTh>
            <GuideTh>{tr('columns.sample')}</GuideTh>
            <GuideTh>{tr('columns.service')}</GuideTh>
            <GuideTh>{tr('columns.lastUsed')}</GuideTh>
            <GuideTh>{tr('columns.modifiedBy')}</GuideTh>
            <th className={cell('actions')} scope="col">
              <span className="pci-sr">{tr('columns.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody aria-busy={loading || undefined}>
          {items.map((mapping) => (
            <tr key={mapping.id} data-testid={`psm-row-${mapping.id}`}>
              <td className="ichtys-num">
                <span className="pci-review-name">{mapping.sourceKey}</span>
              </td>
              <td>
                <StatusPill tone="neutral" className="pci-review-pill">
                  {kindLabel(mapping)}
                </StatusPill>
              </td>
              <td>
                <span className="pci-review-name">{mapping.sampleText || <span className="pci-secondary-cell">—</span>}</span>
              </td>
              <td data-testid={`psm-service-${mapping.id}`}>{serviceText(mapping)}</td>
              <td className="ichtys-num pci-nowrap">{formatDateTime(mapping.lastUsedAt)}</td>
              <td>{mapping.lastModifiedBy || <span className="pci-secondary-cell">—</span>}</td>
              <td className={cell('actions')} data-testid={`psm-actions-${mapping.id}`}>
                <GuideRowActions
                  label={tr('rowActions', { key: mapping.sourceKey })}
                  actions={[
                    {
                      key: 'change',
                      label: tr('change'),
                      onClick: () => {
                        setSaveError(undefined);
                        setEditing(mapping);
                      },
                    },
                    {
                      key: 'delete',
                      label: tr('delete'),
                      danger: true,
                      onClick: () => {
                        setDeleteError(undefined);
                        setDeleting(mapping);
                      },
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </GuideTable>
    );
  };

  return (
    <GuideDrawerSection title={tr('title')} description={tr('description')} testId="psm-section">
      <div className="pci-review-filters">
        <Input
          type="search"
          className="pci-review-search"
          contentBefore={<Search20Regular />}
          value={text}
          maxLength={200}
          onChange={(_event, data) => onType(data.value)}
          aria-label={tr('search')}
          placeholder={tr('searchHelp')}
          data-testid="psm-search"
        />
      </div>
      <div className="pci-review-apply-status" aria-live="polite" data-testid="psm-notice">
        {notice ?? ''}
      </div>
      {loadError !== undefined && items.length > 0 && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="psm-load-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('loadError')}</MessageBarTitle>
            {getApiErrorMessage(loadError)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => setReloadToken((value) => value + 1)}>{tr('retry')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}
      {renderBody()}
      <MedicalServiceChoiceDialog
        open={!!editing}
        mode="pick"
        allowCreate={false}
        title={tr('changeTitle', { key: editing?.sourceKey ?? '' })}
        description={editing?.sampleText}
        initialServiceId={editing?.medicalServiceId}
        busy={saving}
        error={saveError}
        onDismiss={() => setEditing(undefined)}
        onConfirm={(choice) => editing && 'medicalServiceId' in choice && saveService(editing, choice.medicalServiceId)}
      />
      <Dialog open={!!deleting} modalType="alert" onOpenChange={(_event, data) => !data.open && !removing && setDeleting(undefined)}>
        <DialogSurface data-testid="psm-delete-dialog">
          <DialogBody>
            <DialogTitle>{tr('deleteTitle')}</DialogTitle>
            <DialogContent>
              <div className="pci-stack">
                <p>{tr('deleteText', { key: deleting?.sourceKey ?? '', service: deleting ? serviceText(deleting) : '' })}</p>
                {deleteError !== undefined && (
                  <GuideMessageBar intent="error" layout="multiline" data-testid="psm-delete-error">
                    <MessageBarBody>
                      <MessageBarTitle>{tr('deleteError')}</MessageBarTitle>
                      {getApiErrorMessage(deleteError)}
                    </MessageBarBody>
                  </GuideMessageBar>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleting(undefined)} disabled={removing} data-testid="psm-delete-cancel">
                {tr('cancel')}
              </Button>
              <Button
                appearance="secondary"
                className="pci-danger"
                icon={<Delete20Regular />}
                onClick={remove}
                disabled={removing}
                aria-busy={removing || undefined}
                data-testid="psm-delete-confirm"
              >
                {removing ? tr('deleting') : tr('deleteConfirm')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </GuideDrawerSection>
  );
};

export default ProviderServiceMappingsSection;
